import {
  FIRST_POLL_DELAY_MILLISECONDS, MAXIMUM_POLL_DELAY_MILLISECONDS, POLL_DEADLINE_MILLISECONDS, withRefresh,
} from 'components/frank/utils/sectionAnswerPolling.js';

// ASKING FOR A WHOLE SECTION'S BEST SCORES AT ONCE.
//
// Every distinct manufacturing line the listing offers needs a best score, each is computed on the
// manufacturing_cost_calculator queue, and each therefore has to be asked for until it lands. Asked one at a
// time that is one poll loop per manufacturing line: measured on the dev server, sixteen answers cost a hundred
// requests in a good run and six hundred in a slow one. Each is cheap on its own, but it is forty times more
// asking than the information warrants, and it grows with how long the slowest manufacturing line takes rather
// than with how much there is to say.
//
// So one loop asks about all of them and reports each answer AS IT LANDS. Nothing else changes: the server
// still claims, queues, computes and stores each answer exactly as a single ask does, and each is still filed
// under its own per-manufacturing-line URL, so the response cache and its IndexedDB half go on working
// unchanged.

// The most manufacturing lines one ask may name (Frank::SectionBestScores::MAXIMUM_MANUFACTURING_LINES), and the
// longest its query string may grow -- the web server refuses one past 10 KB outright, which two hundred names
// already exceed. A section listing more -- a dynamically nested one lists a sub_section for every set of its
// parts -- is asked about in as many asks as keep under both, and their answers are taken together.
export const MAXIMUM_MANUFACTURING_LINES_PER_ASK = 200;
export const MAXIMUM_QUERY_LENGTH_PER_ASK = 8000;

const keyParameter = (key) => `&manufacturing_lines[]=${encodeURIComponent(key)}`;

const askGroups = (url, keys) => {
  const baseLength = url.length - url.indexOf('?');
  const groups = [];
  let group = [];
  let length = baseLength;
  keys.forEach((key) => {
    const parameterLength = keyParameter(key).length;
    if (group.length > 0 && (group.length === MAXIMUM_MANUFACTURING_LINES_PER_ASK || length + parameterLength > MAXIMUM_QUERY_LENGTH_PER_ASK)) {
      groups.push(group);
      group = [];
      length = baseLength;
    }
    group.push(key);
    length += parameterLength;
  });
  if (group.length > 0) { groups.push(group); }
  return groups;
};

// One poll of the section, for the manufacturing lines still outstanding.
const askAbout = (url, keys, signal) => Promise.all(askGroups(url, keys).map((group) => askAboutGroup(url, group, signal)))
  .then((answers) => Object.assign({}, ...answers));

const askAboutGroup = (url, keys, signal) => fetch(`${url}${keys.map(keyParameter).join('')}`, { signal })
  .then((response) => {
    // A non-OK response is a FAILURE, not an answer -- reading it as one would tell every card in the batch
    // that its manufacturing line cannot be made.
    if (!response.ok) {
      const failure = new Error(`section best scores responded ${response.status}`);
      // WHETHER ASKING AGAIN COULD EVER HELP. A 4xx is the request itself being refused -- the session has
      // expired, or the ask names more manufacturing lines than the endpoint takes
      // (Frank::SectionBestScores::TooManyManufacturingLines) -- and repeating it verbatim can only be refused
      // again. Retried for the grace below, such a section spends thirty seconds looking as if it were being
      // computed and is then reported as if the computation had failed, when the truth was known at once.
      failure.permanent = response.status >= 400 && response.status < 500;
      throw failure;
    }

    return response.json();
  })
  .then((data) => data?.answers || {});

// HOW LONG THE POLLS MAY GO ON FAILING before the manufacturing lines still outstanding are given up on.
//
// Giving up is not a small act here: one poll asks about the WHOLE section, so abandoning it reports every
// unanswered manufacturing line as lost at once -- and a lost score draws the same em dash as "cannot be
// manufactured". This was a count of three failures, which is about a second of a server restarting, and it
// wiped every card a slow section had not yet scored; the per-manufacturing-line asking it replaced could only
// ever lose one.
//
// Measured in TIME rather than in polls because time is what the choice is actually about -- how long a
// struggling server is given to come back -- and a count says that only by way of the backoff below, so
// retuning the backoff would silently retune this.
//
// Thirty seconds because that is the scale of the outage this has to survive: a dyno restarting takes tens of
// seconds, and a section scored across one must not come back with every unanswered card marked
// unmanufacturable. Ending sooner buys nothing anyone wants -- the cards say the same wrong thing sooner --
// while POLL_DEADLINE_MILLISECONDS remains the real backstop for a server that never comes back.
const POLL_FAILURE_GRACE_MILLISECONDS = 30000;

const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', onAbort);
    resolve();
  }, milliseconds);

  function onAbort() {
    clearTimeout(timer);
    reject(signal.reason instanceof Error ? signal.reason : new DOMException('Aborted', 'AbortError'));
  }

  if (signal?.aborted) { onAbort(); return; }
  signal?.addEventListener('abort', onAbort, { once: true });
});

// Poll until every manufacturing line has answered. ONE ROUND of the asking: the caller decides what to do with
// the manufacturing lines this loop ran out of patience for, and /mcc's listing asks about them again rather
// than treating a lost answer as a finding (SCORING_PATIENCE_MILLISECONDS in ManufacturingLineLists).
//
// `onAnswer(key, data)` fires the moment one lands -- that is what keeps the cards filling in one by one, which
// is the whole reason the scores were fetched separately in the first place. `onLost(key, { reason, permanent })`
// fires for the ones that never arrive: a worker killed mid-job writes nothing, and without a deadline its card
// would sit on a placeholder rather than falling through to the caller's own recovery. The reason travels with it
// because the caller has to be able to say why a card has no score, and `permanent` because only this loop knows
// whether the section was REFUSED (asking again is pointless) or merely unanswered (asking again is the recovery).
//
// A failed POLL is not a failed answer -- the request was cut, the server was restarting -- so it is retried
// (see CONSECUTIVE_POLL_FAILURE_LIMIT) before the whole batch is given up on. Aborting stops everything,
// including the wait.
export const pollSectionBestScores = (
  url, keys, { signal, onAnswer, onLost, refresh = false, now = () => Date.now() } = {}
) => {
  const startedAt = now();
  const outstanding = new Set(keys);
  let delay = FIRST_POLL_DELAY_MILLISECONDS;
  // When the polls started failing, or null while they are answering. Cleared by any poll that answers, so the
  // grace above is about a server that is DOWN rather than about failures adding up over a long section.
  let failingSince = null;
  // What the polls have been failing with, so giving up can say what went wrong rather than only that it did.
  let lastFailure = null;
  // ONE-SHOT: only the first poll asks to drop stuck claims. Sending it on every poll would delete the fresh claim
  // the previous poll just made and re-queue the work each time, so the section would never settle.
  let refreshNextPoll = refresh;

  // Report every manufacturing line still outstanding as lost, with WHY and with whether asking again could ever
  // help. `permanent` is the difference between a request that was refused -- which the next ask will be too --
  // and silence, which says nothing about whether the answer is coming; the caller asks again through the second
  // and not the first.
  const giveUpOnTheRest = (reason, permanent = false) => {
    outstanding.forEach((key) => onLost?.(key, { reason: reason, permanent: permanent }));
    outstanding.clear();
  };

  const poll = () => {
    if (outstanding.size === 0) { return Promise.resolve(); }

    const pollUrl = refreshNextPoll ? withRefresh(url) : url;
    refreshNextPoll = false;

    return askAbout(pollUrl, [...outstanding], signal)
      .then((answers) => {
        failingSince = null;
        lastFailure = null;
        Object.entries(answers).forEach(([key, answer]) => {
          if (!outstanding.has(key)) { return; }
          // 'processing' is not an answer, it is the server saying it is still working.
          if (answer?.status === 'processing') { return; }

          outstanding.delete(key);
          // A FAILED answer is reported as lost rather than as a score: the manufacturing line was not found
          // unmanufacturable, the computation broke, and the two must not look the same on a card.
          if (answer?.status === 'failed') {
            // A failure the server REPORTED can still be worth asking about again -- it re-runs the work rather
            // than re-reading the error, because a reported failure is cleared when it is read
            // (Frank::SectionAnswer#call) -- so it is not marked permanent. What bounds that is the caller's
            // patience, not this loop.
            onLost?.(key, { reason: answer.error || 'the score could not be computed', permanent: false });
            return;
          }

          onAnswer?.(key, answer);
        });
      })
      .catch((error) => {
        if (signal?.aborted || error?.name === 'AbortError') { throw error; }

        lastFailure = error?.message || 'the section could not be asked';
        // A REFUSAL ENDS THE BATCH AT ONCE. Nothing about waiting changes a 4xx, and the grace below exists for
        // a server that is coming back -- spending it on a request that will be refused identically only delays
        // telling the caller what is already known.
        if (error?.permanent) { giveUpOnTheRest(lastFailure, true); return; }

        failingSince = failingSince ?? now();
        if (now() - failingSince >= POLL_FAILURE_GRACE_MILLISECONDS) { giveUpOnTheRest(lastFailure); }
      })
      .then(() => {
        if (outstanding.size === 0) { return undefined; }

        if (now() - startedAt >= POLL_DEADLINE_MILLISECONDS) {
          giveUpOnTheRest(lastFailure || 'the score did not arrive');
          return undefined;
        }

        return wait(delay, signal).then(() => {
          delay = Math.min(delay * 1.15, MAXIMUM_POLL_DELAY_MILLISECONDS);
          return poll();
        });
      });
  };

  return poll();
};
